/**
 * Auto Login System
 * 
 * This system automatically handles admin login without manual intervention.
 * It will create the admin user if it doesn't exist and automatically log them in.
 */

import { AuthService } from '../services/auth';

const env = (import.meta as any).env || {};
const ADMIN_EMAIL = env.VITE_ADMIN_EMAIL || '';
const ADMIN_PASSWORD = env.VITE_ADMIN_PASSWORD || '';
const ENABLE_ADMIN_AUTOMATION = env.VITE_ENABLE_ADMIN_AUTOMATION === 'true';

const automationEnabled = () =>
  ENABLE_ADMIN_AUTOMATION && ADMIN_EMAIL.length > 0 && ADMIN_PASSWORD.length > 0;

export const autoLogin = async (): Promise<{
  success: boolean;
  message: string;
  user?: any;
}> => {
  if (!automationEnabled()) {
    return {
      success: false,
      message: 'Admin automation disabled',
    };
  }

  console.log('🔄 Auto-login system starting...');
  
  try {
    // Step 1: Try to get current user
    console.log('🔍 Step 1: Checking current user...');
    const currentUser = await AuthService.getCurrentUser();
    
    if (currentUser && currentUser.email === ADMIN_EMAIL) {
      console.log('✅ Already logged in as admin');
      return {
        success: true,
        message: 'Already logged in as admin',
        user: currentUser
      };
    }
    
    // Step 2: Try to sign in with admin credentials
    console.log('🔍 Step 2: Attempting admin sign in...');
    try {
      const signInResult = await AuthService.signIn(ADMIN_EMAIL, ADMIN_PASSWORD);
      
      if (signInResult.success) {
        console.log('✅ Admin sign in successful');
        return {
          success: true,
          message: 'Admin sign in successful',
          user: signInResult.user
        };
      } else {
        console.log('ℹ️ Admin sign in failed:', signInResult.error);
      }
    } catch (signInError) {
      console.log('ℹ️ Admin sign in error:', signInError);
    }
    
    // Step 3: Create admin user if sign in failed
    console.log('🔍 Step 3: Creating admin user...');
    const createResult = await AuthService.createAdminUser(
      ADMIN_EMAIL,
      ADMIN_PASSWORD,
      'Admin',
      'User',
      '+233123456789',
      'super_admin'
    );
    
    if (createResult.success) {
      console.log('✅ Admin user created successfully');
      return {
        success: true,
        message: 'Admin user created and logged in successfully',
        user: createResult.user
      };
    } else {
      console.error('❌ Failed to create admin user:', createResult.error);
      return {
        success: false,
        message: createResult.error || 'Failed to create admin user'
      };
    }
    
  } catch (error: any) {
    console.error('❌ Auto-login error:', error);
    return {
      success: false,
      message: error.message || 'Auto-login failed'
    };
  }
};

/**
 * Force admin login - bypasses all checks and forces login
 */
export const forceAdminLogin = async (): Promise<{
  success: boolean;
  message: string;
  user?: any;
}> => {
  if (!automationEnabled()) {
    return {
      success: false,
      message: 'Admin automation disabled',
    };
  }

  console.log('🚀 Force admin login starting...');
  
  try {
    // First, try to sign out any existing user
    try {
      await AuthService.signOut();
      console.log('✅ Signed out existing user');
    } catch (error) {
      console.log('ℹ️ No existing user to sign out');
    }
    
    // Try to sign in
    console.log('🔍 Attempting admin sign in...');
    const signInResult = await AuthService.signIn(ADMIN_EMAIL, ADMIN_PASSWORD);
    
    if (signInResult.success) {
      console.log('✅ Force admin login successful');
      return {
        success: true,
        message: 'Force admin login successful',
        user: signInResult.user
      };
    }
    
    // If sign in failed, create user
    console.log('🔍 Creating admin user...');
    const createResult = await AuthService.createAdminUser(
      ADMIN_EMAIL,
      ADMIN_PASSWORD,
      'Admin',
      'User',
      '+233123456789',
      'super_admin'
    );
    
    if (createResult.success) {
      console.log('✅ Admin user created via force login');
      return {
        success: true,
        message: 'Admin user created via force login',
        user: createResult.user
      };
    } else {
      console.error('❌ Force login failed:', createResult.error);
      return {
        success: false,
        message: createResult.error || 'Force login failed'
      };
    }
    
  } catch (error: any) {
    console.error('❌ Force login error:', error);
    return {
      success: false,
      message: error.message || 'Force login failed'
    };
  }
};

// Make functions available globally
if (typeof window !== 'undefined' && automationEnabled()) {
  (window as any).autoLogin = autoLogin;
  (window as any).forceAdminLogin = forceAdminLogin;
  console.log('🔄 Auto-login functions available (automation enabled):');
  console.log('  - autoLogin() - Automatic admin login');
  console.log('  - forceAdminLogin() - Force admin login');
}

export default autoLogin;
